import React, { useMemo, useState } from "react";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import {
  getMoonPhase,
  getNextFullMoon,
  getNextNewMoon,
} from "../../utils/moonPhase";

interface MoonPhaseCardProps {
  className?: string;
  cycleDay?: number; // Optional: current cycle day for alignment info
}

const MoonPhaseCard: React.FC<MoonPhaseCardProps> = ({
  className = "",
  cycleDay,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const moonData = useMemo(() => getMoonPhase(), []);
  const nextFullMoon = useMemo(() => getNextFullMoon(), []);
  const nextNewMoon = useMemo(() => getNextNewMoon(), []);

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  // Calculate days until next major phase
  const daysUntilFullMoon = Math.ceil(
    (nextFullMoon.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24),
  );
  const daysUntilNewMoon = Math.ceil(
    (nextNewMoon.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24),
  );

  return (
    <div
      className={`bg-base-100 sm:rounded-lg shadow-sm overflow-hidden ${className}`}
    >
      <div className="p-6">
        {/* Header - Always Visible */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between  group"
        >
          <div className="flex items-center">
            <div>
              <div className="flex items-center gap-3">
                <h3 className="text-sm font-grotesk font-light text-base-content text-left">
                  Moon Phase
                </h3>
              </div>
              {!isExpanded && (
                <div className="flex items-center gap-3">
                  <span className="text-lg font-heading font-semibold text-base-content-600">
                    {moonData.phaseName}
                  </span>

                  <span
                    className="text-3xl"
                    role="img"
                    aria-label={moonData.phaseName}
                  >
                    {moonData.phaseEmoji}
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isExpanded ? (
              <ChevronUpIcon className="w-5 h-5 text-base-content/50 group-hover:text-base-content/80 transition-colors" />
            ) : (
              <ChevronDownIcon className="w-5 h-5 text-base-content/50 group-hover:text-base-content/80 transition-colors" />
            )}
          </div>
        </button>

        {/* Collapsible Content */}
        {isExpanded && (
          <div className="space-y-4">
            {/* Current Phase */}
            <div>
              <p className="text-2xl font-heading font-bold text-indigo-600 mb-1">
                {moonData.phaseName}
              </p>
              <p className="text-sm text-base-content/80 font-sans">
                {moonData.description}
              </p>
            </div>

            {/* Moon Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-base-100 rounded-md p-3">
                <p className="text-xs text-base-content/60 font-sans mb-1">
                  Illumination
                </p>
                <p className="text-lg font-heading font-semibold text-base-content">
                  {moonData.illumination}%
                </p>
              </div>
              <div className="bg-base-100 rounded-md p-3">
                <p className="text-xs text-base-content/60 font-sans mb-1">
                  Lunar Day
                </p>
                <p className="text-lg font-heading font-semibold text-base-content">
                  {Math.round(moonData.age)}
                </p>
              </div>
            </div>

            {/* Next Phases */}
            <div className="space-y-2 pt-3 border-t border-base-200 pb-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-base-content/80 font-sans flex items-center gap-1">
                  <span>🌕</span> Next Full Moon
                </span>
                <span className="font-heading font-semibold text-base-content">
                  {formatDate(nextFullMoon)}
                  <span className="text-xs text-base-content/60 ml-1">
                    ({daysUntilFullMoon}d)
                  </span>
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-base-content/80 font-sans flex items-center gap-1">
                  <span>🌑</span> Next New Moon
                </span>
                <span className="font-heading font-semibold text-base-content">
                  {formatDate(nextNewMoon)}
                  <span className="text-xs text-base-content/60 ml-1">
                    ({daysUntilNewMoon}d)
                  </span>
                </span>
              </div>
            </div>

            {/* Cycle Alignment (if cycle day is provided) */}
            {/* {cycleDay && (
              <div className="pt-3 border-t border-base-200">
                <p className="text-xs text-indigo-700 font-sans">
                  <strong className="font-semibold">Moon & Cycle:</strong>{" "}
                  {getMoonCycleMessage(cycleDay, moonData.phaseName)}
                </p>
              </div>
            )} */}
            <div className="bg-indigo-50 p-4 rounded-md">
              <p className="text-sm font-sans text-indigo-800 font-semibold">
                Disclaimer
              </p>
              <p className="text-xs text-indigo-800">
                This feature explores the relationship between lunar cycles and
                your menstrual cycle. This is a connection documented in ancient
                wisdom and personal observation. While not yet scientifically
                verified, many find that tracking moon phases offers interesting
                insights into the body's natural rhythms and intuition. We
                encourage you to observe any patterns that feel meaningful to
                you.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Helper function for cycle alignment message
function getMoonCycleMessage(cycleDay: number, moonPhase: string): string {
  // New moon typically aligns with menstruation (days 1-5)
  if (cycleDay >= 1 && cycleDay <= 5 && moonPhase === "New Moon") {
    return "Your cycle aligns with the new moon - a powerful time for renewal and rest.";
  }

  // Full moon typically aligns with ovulation (days 12-16)
  if (cycleDay >= 12 && cycleDay <= 16 && moonPhase === "Full Moon") {
    return "Your cycle aligns with the full moon - peak energy and vitality.";
  }

  // Waxing phases align with follicular phase
  if (
    cycleDay >= 6 &&
    cycleDay <= 13 &&
    (moonPhase === "Waxing Crescent" ||
      moonPhase === "First Quarter" ||
      moonPhase === "Waxing Gibbous")
  ) {
    return "Both moon and cycle are in growth phases - building energy.";
  }

  // Waning phases align with luteal phase
  if (
    cycleDay >= 17 &&
    (moonPhase === "Waning Gibbous" ||
      moonPhase === "Last Quarter" ||
      moonPhase === "Waning Crescent")
  ) {
    return "Both moon and cycle are in reflective phases - time for rest.";
  }

  return "Moon and cycle phases are independent but both influence your energy.";
}

export default MoonPhaseCard;
